/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */
import React, { useEffect, useRef, useContext } from 'react';
import ReactRouterPropTypes from 'react-router-prop-types';
import { hot } from 'react-hot-loader/root';
import { withRouter } from 'react-router-dom';
import Nav from './components/Nav';
import LoginBanner from './components/LoginBanner';
import MainRouter from './containers/MainRouter';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import StoreContext from "./context/StoreContext";
import SmsContext from './context/SmsContext';
import WorkOrderContext from './context/WorkOrderContext';
import FeedbackContext from './context/FeedbackContext';
import { Alert, Snackbar } from '@mui/material';
import UserContext from './context/UserContext';
import { checkWOsData, getRandomTime, updateCourtesyRidesData } from './helpers/WorkOrders';
import { createReconnectingSocket } from './helpers/ReconnectingSocket';
import { useIdleTimer } from 'react-idle-timer'
import { LYFT_VALID_CANCEL_REASONS, MESSAGE_TYPE, PING_TYPE } from './utils/constants';
import Loading from './components/Loading';

function App(props) {
  const { isAdmin, isLoggedIn } = useContext(UserContext);
  const {isLoading, showErrorAlert, setIsLoading, setShowErrorAlert} = useContext(FeedbackContext);
  const { storeId, updateStore } = useContext(StoreContext);
  const { setSendingSms, setSwitchToUpdate, setMessageType } = useContext(SmsContext);
  const { fetchWorkOrders, updateWorkOrderFields, workOrders, setWorkOrders, setCanceledLyftWorkOrder } = useContext(WorkOrderContext);
  const timeoutInMinutes = (1000 * 60 * 10) + (1000 * 60 * getRandomTime(0,10))
  
  const storeRef = useRef(storeId);
  // The socket outlives renders, so it calls the latest handler through a ref instead of a stale closure.
  const socketMessageReceivedRef = useRef(null);

  const handleOnIdle = async () => {
    console.log('User is idle');
    setIsLoading(true);
    await fetchWorkOrders(storeId);
    setIsLoading(false)
    useTimer.reset();
  };

  const useTimer = useIdleTimer({
    timeout: timeoutInMinutes,  //10 minutes default time for the first request
    onIdle: handleOnIdle,
    debounce: 500
  });

  useEffect(() => {
    const fetch = async () => {
      setIsLoading(true);
      await fetchWorkOrders(storeId);
      setIsLoading(false);
    };
    if (+storeId === 1 || !storeId) return;

    storeRef.current = storeId;
    fetch();
    const socket = createReconnectingSocket(`${process.env.REACT_APP_WEBSOCKET_URL}?storeId=${storeId}`, {
      storeId,
      onMessage: e => socketMessageReceivedRef.current(e),
    });

    return () => socket.close(1000, `switching from store ${storeId}`);
  }, [storeId]);

  const socketMessageReceived = async (event) => { 
    //console.log("==socket message event: ", event)
    const socketData = JSON.parse(event.data);
    console.log('Socket site: ', socketData.siteNo, ' | Store site: ', storeRef.current, '  Socket data: ', socketData);
    if (socketData && parseInt(socketData.siteNo) === parseInt(storeRef.current)) {

      if(socketData.WorkOrderIngest){
        setWorkOrders((prevWOs) => {
          const WOs = checkWOsData(prevWOs, socketData.WorkOrderIngest);
          return WOs;
        })
      }

      // Phone number update
      if(socketData.newWo) {
        setWorkOrders((oldWOs) => {
          let WOsToUpdate = [...oldWOs];
          const WOExists = oldWOs.findIndex(wo => wo.WO === socketData.newWo.WO);
          if(WOExists !== -1) {
            WOsToUpdate[WOExists].Phone = socketData.newWo.PhoneNumber;
          }
          return WOsToUpdate;
        })
      }

      if(socketData.WOWithSMSStatus){
        console.log('===WOs to update',socketData.WOWithSMSStatus.WO)
        const WOToUpdate = socketData.WOWithSMSStatus.WO.split(',')
        const messageType = socketData.WOWithSMSStatus.Type;
        setSendingSms((prev) =>{ return prev.filter(WO => {
            return !(WOToUpdate.includes(WO.wo) && messageType.includes(WO.messageType))
          }
        )})
        setSwitchToUpdate({
          WO: WOToUpdate[0], 
          smsType: socketData.WOWithSMSStatus.Type, 
          smsData: {
            Message: socketData.WOWithSMSStatus.StatusMessage, 
            Status: socketData.WOWithSMSStatus.Status, 
            Timestamp: socketData.WOWithSMSStatus.last_modified_date
          }
        })

        // Update the customer_eta of the existing WO
        if (socketData.WOWithSMSStatus.Type === MESSAGE_TYPE.promisedTimeMessage || socketData.WOWithSMSStatus.Type === MESSAGE_TYPE.promisedTimeMessageUpdate) {
          setWorkOrders((oldWOs) => {
            const WOExists = oldWOs.findIndex(wo => wo.WO === socketData.WOWithSMSStatus.WO);
            if(WOExists !== -1) {
              oldWOs[WOExists].customer_eta = socketData.WOWithSMSStatus.message_customer_eta;
            }
            return oldWOs;
          })
        }

      }
      if(socketData.featureFlagUpdate){
        console.log('===Feature flag modified',socketData.featureFlagUpdate)
        await updateStore(socketData.siteNo, isAdmin)
      }

      if (socketData.pingType === PING_TYPE.VehicleReadySmsQueued) {
        console.log(`Deactivate VR switch: ${JSON.stringify(socketData)} for ${process.env.REACT_APP_VEHICLE_READY_DELAY} seconds`);
        const WOToUpdate = socketData.WO;
        const messageType = 'vehicleReadyMessage'
        setMessageType('vehicleReadyMessage')
        setSendingSms((prev) =>{ 
          return [
            ...prev, 
            {
              wo: WOToUpdate,
              messageType: messageType
            }
          ]
        })
        updateWorkOrderFields(WOToUpdate, {cf_order_status: 'C', cf_completed_timestamp: socketData.cfCompletedTimestamp})
      }

      // Courtesy Rides ping
      if (socketData[PING_TYPE.CourtesyRide.NewRide]) {
        console.log('Updating WO with Courtesy Ride data', socketData.NewRide)
        const courtesyRidesItem = socketData.NewRide;
        setWorkOrders((prevWOs) => {
          const WOs = updateCourtesyRidesData(prevWOs, socketData.NewRide.work_order_id, courtesyRidesItem);
          return WOs;
        })
      } 
      
      if(socketData[PING_TYPE.CourtesyRide.UpdateRide]) {
        console.log('Updating WO Ride updates', socketData.UpdateRide)
        const courtesyRidesItem = socketData.UpdateRide;
        setWorkOrders((prevWOs) => {
          const WOs = updateCourtesyRidesData(prevWOs, socketData.UpdateRide.work_order_id, courtesyRidesItem);
          return WOs;
        })
      }
      
      if(socketData[PING_TYPE.CourtesyRide.CancelRide]) {
        console.log('The ride has been canceled')
        const courtesyRidesItem = socketData.CancelRide;
        const isForRequestRideAgain = LYFT_VALID_CANCEL_REASONS.includes(courtesyRidesItem?.canceled_by)
        setWorkOrders((prevWOs) => {
          const WOs = updateCourtesyRidesData(prevWOs, socketData.CancelRide.work_order_id, courtesyRidesItem);
          if(isForRequestRideAgain) {
            const WOIndex = WOs.findIndex(wo => wo.WO === socketData.CancelRide.work_order_id);
            setCanceledLyftWorkOrder(WOs[WOIndex])
          }
          return WOs;
        })
      }


    }
  };
  socketMessageReceivedRef.current = socketMessageReceived;


  return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {isLoggedIn ? (
          <Nav/>
        ) : (
          <LoginBanner />
        )}
        <MainRouter
          {...props} 
          currentWorkOrders={workOrders}
        />
        <Snackbar open={!!showErrorAlert} autoHideDuration={6000} onClose={() => setShowErrorAlert(false)}>
          <Alert variant="filled" elevation={2} severity="error">{showErrorAlert}</Alert>
        </Snackbar>

        {
          isLoading && (
            <Loading isLoading={isLoading} />
          )
        }
        
      </ThemeProvider>
  );
}
//note this is the node_env which by default is set to development, not related to the stage.
export default process.env.NODE_ENV === 'development'
  ? hot(withRouter(App))
  : withRouter(App);
App.propTypes = {
  history: ReactRouterPropTypes.history,
};
